import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { api_endpoint } from "../constants/constant.jsx";
import { fetchAllPaginatedItems } from "../lib/fetchAllPaginatedItems";


function normalizeRole(role) {
    if (typeof role !== "string") return null;
    const trimmed = role.trim();
    if (!trimmed) return null;
    const lower = trimmed.toLowerCase();
    if (lower === "tm" || lower === "t.m" || lower === "t/m") return "Tm";
    return trimmed.charAt(0).toUpperCase() + trimmed.slice(1);
}

function base64UrlDecode(value) {
    if (typeof value !== "string") return "";
    const normalized = value.replace(/-/g, "+").replace(/_/g, "/");
    const padLen = (4 - (normalized.length % 4)) % 4;
    const padded = normalized + "=".repeat(padLen);
    // Browser env (Vite) will have atob.
    if (typeof globalThis.atob === "function") {
        return globalThis.atob(padded);
    }
    // Fallback for non-browser envs.
    // eslint-disable-next-line no-undef
    return Buffer.from(padded, "base64").toString("utf8");
}

function decodeJwtPayload(token) {
    if (typeof token !== "string") return null;
    const parts = token.split(".");
    if (parts.length < 2) return null;
    try {
        const json = base64UrlDecode(parts[1]);
        return JSON.parse(json);
    } catch {
        return null;
    }
}

function extractRolesFromToken(token) {
    const payload = decodeJwtPayload(token);
    if (!payload || typeof payload !== "object") return [];

    const candidates = [
        payload.roles,
        payload.role,
        payload.user_roles,
        payload.userRoles,
        payload.public_metadata?.roles,
        payload.publicMetadata?.roles,
        payload.unsafe_metadata?.roles,
        payload.unsafeMetadata?.roles,
        payload.metadata?.roles,
        payload.app_metadata?.roles,
        payload.appMetadata?.roles,
        payload.authorization?.roles,
    ];

    const flatten = (value) => {
        if (!value) return [];
        if (Array.isArray(value)) return value;
        if (typeof value === "string") return value.split(",");
        if (typeof value === "object" && Array.isArray(value.roles)) return value.roles;
        return [];
    };

    const raw = candidates.flatMap(flatten);
    return raw.map(normalizeRole).filter(Boolean);
}

function extractRolesFromResponse(data) {
    // Accepts:
    // - ["Admin"]
    // - { roles: ["Admin"] }
    // - { data: { roles: ["Admin"] } }
    // - { result: { roles: ["Admin"] } }
    // - { roles: "Admin,Instructor" }
    const candidate =
        (Array.isArray(data) ? data : null) ??
        (Array.isArray(data?.roles) ? data.roles : null) ??
        (Array.isArray(data?.data?.roles) ? data.data.roles : null) ??
        (Array.isArray(data?.result?.roles) ? data.result.roles : null);

    const list =
        candidate ??
        (typeof data?.roles === "string" ? data.roles.split(",") : null) ??
        (typeof data?.data?.roles === "string" ? data.data.roles.split(",") : null);

    if (!Array.isArray(list)) return [];
    return list
        .map(normalizeRole)
        .filter(Boolean);
}


// Thunk to fetch roles for a given Clerk user id
export const fetchRolesByUserId = createAsyncThunk(
    "auth/fetchRolesByUserId",
    async ({ userId, authToken, userEmail = null, userUsername = null }, { rejectWithValue }) => {
        try {
            if (!userId) throw new Error("Missing userId for roles fetch");
            if (!authToken) throw new Error("Missing auth token for roles fetch");

            const headers = {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            };

            const tryFetch = async (url) => {
                const res = await axios.get(url, { headers });
                const data = res.data;
                if (import.meta?.env?.DEV) console.log("RolesBootstrap: roles response", url, data);
                return extractRolesFromResponse(data);
            };

            // Primary endpoint (legacy / existing)
            const primaryUrl = `${api_endpoint}/user/${userId}/roles`;
            let apiRoles = await tryFetch(primaryUrl);
            let backendUserId = null;

            const norm = (v) => String(v || "").trim().toLowerCase();
            const targetClerkId = norm(userId);
            const targetEmail = norm(userEmail);
            const targetUsername = norm(userUsername);

            const userMatchesIdentity = (u) => {
                const clerk = norm(u?.clerk_id ?? u?.clerkId ?? u?.clerkID);
                const email = norm(u?.email);
                const username = norm(u?.username);
                return (
                    (targetClerkId && clerk && clerk === targetClerkId) ||
                    (targetEmail && email && email === targetEmail) ||
                    (targetUsername && username && username === targetUsername)
                );
            };

            // If backend returns empty roles for a Clerk id, it often means the endpoint expects
            // the backend's numeric user id. Map Clerk id/email -> backend id.
            if (apiRoles.length === 0) {
                let mappingError = null;
                try {
                    const selectUsers = (data) => data?.users ?? [];
                    const fetchUsersList = async (url) => {
                        return await fetchAllPaginatedItems({
                            url,
                            token: authToken,
                            selectItems: selectUsers,
                            mode: "skip",
                            pageSize: 200,
                            maxPages: 20,
                        });
                    };

                    // Try the broad users list first (may be admin-only).
                    let users = [];
                    try {
                        users = await fetchUsersList(`${api_endpoint}/user/users`);
                    } catch (e) {
                        mappingError = e;
                        users = [];
                    }

                    // If the broad list is blocked, fall back to role-scoped endpoints.
                    if (!Array.isArray(users) || users.length === 0) {
                        const candidateUrls = [
                            `${api_endpoint}/user/users/role/Instructor`,
                            `${api_endpoint}/user/users/role/Manager`,
                            `${api_endpoint}/user/users/role/Admin`,
                        ];
                        const pools = await Promise.all(
                            candidateUrls.map(async (url) => {
                                try {
                                    return await fetchUsersList(url);
                                } catch (e) {
                                    if (!mappingError) mappingError = e;
                                    return [];
                                }
                            })
                        );
                        users = pools.flat();
                    }

                    const match = (users || []).find((u) => {
                        return userMatchesIdentity(u);
                    });

                    const candidateId = match?.id ?? match?.user_id ?? null;
                    backendUserId = candidateId != null ? Number(candidateId) : null;

                    if (import.meta?.env?.DEV) {
                        console.log("RolesBootstrap: mapped backend user id", {
                            clerkId: userId,
                            userEmail,
                            userUsername,
                            backendUserId,
                            mappingError: mappingError
                                ? {
                                      status: mappingError?.response?.status,
                                      message: mappingError?.message,
                                  }
                                : null,
                            matchedKeys: match
                                ? {
                                      id: match?.id,
                                      user_id: match?.user_id,
                                      clerk_id: match?.clerk_id,
                                      email: match?.email,
                                      username: match?.username,
                                  }
                                : null,
                        });
                    }

                    if (Number.isFinite(backendUserId)) {
                        const backendUrl = `${api_endpoint}/user/${backendUserId}/roles`;
                        apiRoles = await tryFetch(backendUrl);
                    }
                } catch (e) {
                    if (import.meta?.env?.DEV) {
                        console.warn("RolesBootstrap: failed to map backend user id", e);
                    }
                }
            }

            // If roles are still empty, infer them by checking role-scoped lists.
            // These endpoints are already used elsewhere in the app, and often work
            // even when a dedicated roles endpoint is missing/inconsistent.
            let inferredRoles = [];
            if (apiRoles.length === 0) {
                try {
                    const selectUsers = (data) => data?.users ?? [];
                    const fetchUsersList = async (url) => {
                        return await fetchAllPaginatedItems({
                            url,
                            token: authToken,
                            selectItems: selectUsers,
                            mode: "skip",
                            pageSize: 200,
                            maxPages: 20,
                        });
                    };

                    const checks = [
                        { role: "Instructor", url: `${api_endpoint}/user/users/role/Instructor` },
                        { role: "Manager", url: `${api_endpoint}/user/users/role/Manager` },
                        { role: "Admin", url: `${api_endpoint}/user/users/role/Admin` },
                    ];

                    const results = await Promise.all(
                        checks.map(async (c) => {
                            try {
                                const users = await fetchUsersList(c.url);
                                const match = (users || []).some(userMatchesIdentity);
                                return match ? c.role : null;
                            } catch {
                                return null;
                            }
                        })
                    );

                    inferredRoles = results.filter(Boolean).map(normalizeRole);
                    if (import.meta?.env?.DEV) {
                        console.log("RolesBootstrap: inferred roles from role lists:", inferredRoles);
                    }
                } catch {
                    inferredRoles = [];
                }
            }

            const tokenRoles = apiRoles.length > 0 ? [] : extractRolesFromToken(authToken);
            const roles =
                apiRoles.length > 0
                    ? apiRoles
                    : inferredRoles.length > 0
                        ? inferredRoles
                        : tokenRoles;

            if (apiRoles.length === 0) {
                if (import.meta?.env?.DEV) {
                    console.log("RolesBootstrap: token-claim roles:", tokenRoles);
                }
                if (inferredRoles.length > 0) {
                    console.warn("RolesBootstrap: backend returned empty roles; using inferred roles");
                } else if (tokenRoles.length > 0) {
                    console.warn("RolesBootstrap: backend returned empty roles; using token claims");
                }
            }

            return { userId, roles, backendUserId };
        } catch (err) {
            return rejectWithValue(err?.message || "Unknown error fetching roles");
        }
    }
);

const initialState = {
    token: null,
    user: null,
    roles: [], // example: ["Admin", "Instructor", "Manager", "Tm"]
    rolesStatus: "idle", // 'idle' | 'loading' | 'succeeded' | 'failed'
    rolesError: null,
    rolesUserId: null, // tracks which user the roles belong to
    backendUserId: null,
    backendUserIdFor: null,
}

export const authSlice = createSlice({
    name: "auth",
    initialState,
    reducers: {
        setToken: (state, action) => {
            state.token = action.payload
        },
        setUser: (state, action) => {
            state.user = action.payload
        },
        logoutUser: (state, action) => {
            state.token = null
            state.user = null
            state.roles = []
            state.rolesStatus = "idle"
            state.rolesError = null
            state.rolesUserId = null
            state.backendUserId = null
            state.backendUserIdFor = null
        }
    }

    , extraReducers: (builder) => {
        builder
            .addCase(fetchRolesByUserId.pending, (state) => {
                state.rolesStatus = "loading";
                state.rolesError = null;
            })
            .addCase(fetchRolesByUserId.fulfilled, (state, action) => {
                state.rolesStatus = "succeeded";
                state.roles = action.payload.roles || [];
                state.rolesUserId = action.payload.userId || null;
                state.backendUserId =
                    Number.isFinite(action.payload.backendUserId) ? action.payload.backendUserId : null;
                state.backendUserIdFor = action.payload.userId || null;
            })
            .addCase(fetchRolesByUserId.rejected, (state, action) => {
                state.rolesStatus = "failed";
                state.rolesError = action.payload || action.error?.message || "Failed to load roles";
                state.roles = [];
            })
    }
})

export const { setToken, setUser, logoutUser } = authSlice.actions

export default authSlice.reducer